import React, { useState, useEffect } from 'react';
import {
  getReportDataSources,
  getReportTemplates,
  createReportTemplate,
  updateReportTemplate,
  deleteReportTemplate,
  executeCustomReport,
  exportCustomReportExcel
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Database,
  Columns3,
  Filter,
  Play,
  Save,
  Download,
  Trash2,
  Plus,
  X,
  Loader2,
  FileSpreadsheet,
  FileText,
  MoreHorizontal,
  Edit,
  Copy,
  GripVertical,
  ArrowUpDown,
  ChevronRight,
  FolderOpen,
  Settings2,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate, cn } from '@/lib/utils';

const FILTER_OPERATORS = {
  text: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'contains', label: 'Contains' }
  ],
  number: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'less_than', label: 'Less Than' }
  ],
  date: [
    { value: 'equals', label: 'Equals' },
    { value: 'greater_than', label: 'After' },
    { value: 'less_than', label: 'Before' }
  ],
  boolean: [
    { value: 'equals', label: 'Equals' }
  ]
};

export default function ReportBuilderPage() {
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  const [dataSources, setDataSources] = useState({});
  const [templates, setTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState('builder');
  
  // Report configuration
  const [config, setConfig] = useState({
    name: 'Untitled Report',
    description: '',
    data_source: '',
    selected_fields: [],
    filters: [],
    sort_by: '',
    sort_order: 'asc',
    group_by: '',
    is_public: false
  });
  
  // Results
  const [results, setResults] = useState(null);
  
  // Dialogs
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [sourcesRes, templatesRes] = await Promise.all([
        getReportDataSources(),
        getReportTemplates()
      ]);
      setDataSources(sourcesRes.data?.data_sources || {});
      setTemplates(templatesRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load report builder data');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentFields = () => {
    if (!config.data_source || !dataSources[config.data_source]) return [];
    return dataSources[config.data_source].fields || [];
  };

  const handleDataSourceChange = (source) => {
    setConfig(prev => ({
      ...prev,
      data_source: source,
      selected_fields: [],
      filters: [],
      sort_by: '',
      group_by: ''
    }));
    setResults(null);
  };

  const handleFieldToggle = (fieldKey) => {
    setConfig(prev => {
      const fields = prev.selected_fields.includes(fieldKey)
        ? prev.selected_fields.filter(f => f !== fieldKey)
        : [...prev.selected_fields, fieldKey];
      return { ...prev, selected_fields: fields };
    });
  };

  const handleSelectAllFields = () => {
    const allFields = getCurrentFields().map(f => f.key);
    setConfig(prev => ({
      ...prev,
      selected_fields: prev.selected_fields.length === allFields.length ? [] : allFields
    }));
  };

  const handleAddFilter = () => {
    setConfig(prev => ({
      ...prev,
      filters: [...prev.filters, { field: '', operator: 'equals', value: '' }]
    }));
  };

  const handleUpdateFilter = (index, updates) => {
    setConfig(prev => {
      const filters = [...prev.filters];
      filters[index] = { ...filters[index], ...updates };
      return { ...prev, filters };
    });
  };

  const handleRemoveFilter = (index) => {
    setConfig(prev => ({
      ...prev,
      filters: prev.filters.filter((_, i) => i !== index)
    }));
  };

  const handleExecuteReport = async () => {
    if (!config.data_source) {
      toast.error('Please select a data source');
      return;
    }
    if (config.selected_fields.length === 0) {
      toast.error('Please select at least one field');
      return;
    }

    setExecuting(true);
    try {
      const response = await executeCustomReport(config);
      setResults(response.data);
      toast.success(`Report executed: ${response.data.total_rows} rows found`);
    } catch (error) {
      console.error('Error executing report:', error);
      toast.error('Failed to execute report');
    } finally {
      setExecuting(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!config.name || config.name === 'Untitled Report') {
      toast.error('Please enter a report name');
      return;
    }

    setSaving(true);
    try {
      if (editingTemplateId) {
        await updateReportTemplate(editingTemplateId, config);
        toast.success('Template updated');
      } else {
        await createReportTemplate(config);
        toast.success('Template saved');
      }
      setSaveDialogOpen(false);
      setEditingTemplateId(null);
      await loadInitialData();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadTemplate = (template) => {
    setConfig({
      name: template.name,
      description: template.description || '',
      data_source: template.data_source,
      selected_fields: template.selected_fields || [],
      filters: template.filters || [],
      sort_by: template.sort_by || '',
      sort_order: template.sort_order || 'asc',
      group_by: template.group_by || '',
      is_public: template.is_public || false
    });
    setEditingTemplateId(template.id);
    setResults(null);
    setActiveTab('builder');
    toast.success(`Loaded template: ${template.name}`);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await deleteReportTemplate(templateId);
      toast.success('Template deleted');
      await loadInitialData();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleExportExcel = async () => {
    if (!results || results.data.length === 0) {
      toast.error('No data to export');
      return;
    }

    setExporting(true);
    try {
      const response = await exportCustomReportExcel(config);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${config.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleNewReport = () => {
    setConfig({
      name: 'Untitled Report',
      description: '',
      data_source: '',
      selected_fields: [],
      filters: [],
      sort_by: '',
      sort_order: 'asc',
      group_by: '',
      is_public: false
    });
    setEditingTemplateId(null);
    setResults(null);
  };

  const getFieldTypeOperators = (fieldKey) => {
    const field = getCurrentFields().find(f => f.key === fieldKey);
    if (!field) return FILTER_OPERATORS.text;
    return FILTER_OPERATORS[field.type] || FILTER_OPERATORS.text;
  };

  const formatCellValue = (value, fieldKey) => {
    const field = getCurrentFields().find(f => f.key === fieldKey);
    if (value === null || value === undefined) return '-';
    
    if (field?.type === 'number') {
      if (fieldKey.includes('amount') || fieldKey.includes('balance') || fieldKey.includes('fee') || fieldKey.includes('total')) {
        return formatCurrency(value);
      }
      return value.toLocaleString();
    }
    if (field?.type === 'date' && value) {
      return formatDate(value);
    }
    if (field?.type === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  };

  return (
    <div className="space-y-6" data-testid="report-builder-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Custom Report Builder
          </h1>
          <p className="text-muted-foreground mt-1">
            Build custom reports with drag-and-drop fields, filters, and exports
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleNewReport} className="gap-2">
            <Plus className="w-4 h-4" /> New Report
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="builder" className="gap-2">
            <Settings2 className="w-4 h-4" />
            Report Builder
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <FolderOpen className="w-4 h-4" />
            Saved Templates ({templates.length})
          </TabsTrigger>
        </TabsList>

        {/* Builder Tab */}
        <TabsContent value="builder" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel - Configuration */}
            <div className="lg:col-span-1 space-y-4">
              {/* Data Source */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    Data Source
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Select value={config.data_source} onValueChange={handleDataSourceChange}>
                    <SelectTrigger data-testid="select-data-source">
                      <SelectValue placeholder="Select data source..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(dataSources).map(([key, source]) => (
                        <SelectItem key={key} value={key}>
                          {source.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              {/* Fields Selection */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Columns3 className="w-4 h-4" />
                      Fields ({config.selected_fields.length})
                    </CardTitle>
                    {config.data_source && (
                      <Button variant="ghost" size="sm" onClick={handleSelectAllFields}>
                        {config.selected_fields.length === getCurrentFields().length ? 'Deselect All' : 'Select All'}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {!config.data_source ? (
                    <p className="text-sm text-muted-foreground">Select a data source first</p>
                  ) : (
                    <ScrollArea className="h-[200px]">
                      <div className="space-y-2">
                        {getCurrentFields().map(field => (
                          <div
                            key={field.key}
                            className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer"
                            onClick={() => handleFieldToggle(field.key)}
                          >
                            <Checkbox
                              checked={config.selected_fields.includes(field.key)}
                              onCheckedChange={() => handleFieldToggle(field.key)}
                            />
                            <span className="text-sm flex-1">{field.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {field.type}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>

              {/* Filters */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Filter className="w-4 h-4" />
                      Filters ({config.filters.length})
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={handleAddFilter} disabled={!config.data_source}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {config.filters.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No filters added</p>
                  ) : (
                    <div className="space-y-3">
                      {config.filters.map((filter, idx) => (
                        <div key={idx} className="p-3 border rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Filter {idx + 1}</span>
                            <Button variant="ghost" size="sm" onClick={() => handleRemoveFilter(idx)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                          <Select
                            value={filter.field}
                            onValueChange={(v) => handleUpdateFilter(idx, { field: v, operator: 'equals' })}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue placeholder="Field" />
                            </SelectTrigger>
                            <SelectContent>
                              {getCurrentFields().map(f => (
                                <SelectItem key={f.key} value={f.key}>{f.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={filter.operator}
                            onValueChange={(v) => handleUpdateFilter(idx, { operator: v })}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue placeholder="Operator" />
                            </SelectTrigger>
                            <SelectContent>
                              {getFieldTypeOperators(filter.field).map(op => (
                                <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            className="h-8"
                            placeholder="Value"
                            value={filter.value}
                            onChange={(e) => handleUpdateFilter(idx, { value: e.target.value })}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sorting */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ArrowUpDown className="w-4 h-4" />
                    Sorting
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Select
                    value={config.sort_by || 'none'}
                    onValueChange={(v) => setConfig(prev => ({ ...prev, sort_by: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sort by..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No sorting</SelectItem>
                      {config.selected_fields.map(key => {
                        const field = getCurrentFields().find(f => f.key === key);
                        return (
                          <SelectItem key={key} value={key}>{field?.name || key}</SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {config.sort_by && (
                    <Select
                      value={config.sort_order}
                      onValueChange={(v) => setConfig(prev => ({ ...prev, sort_order: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asc">Ascending</SelectItem>
                        <SelectItem value="desc">Descending</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex flex-col gap-2">
                <Button
                  onClick={handleExecuteReport}
                  disabled={executing || !config.data_source || config.selected_fields.length === 0}
                  className="gap-2 bg-gradient-to-r from-[hsl(188,64%,38%)] to-[hsl(164,62%,40%)]"
                  data-testid="execute-report-btn"
                >
                  {executing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Run Report
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setSaveDialogOpen(true)}
                    disabled={!config.data_source}
                    className="flex-1 gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save Template
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleExportExcel}
                    disabled={exporting || !results?.data?.length}
                    className="flex-1 gap-2"
                    data-testid="export-excel-btn"
                  >
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                    Export
                  </Button>
                </div>
              </div>
            </div>

            {/* Right Panel - Results */}
            <div className="lg:col-span-2">
              <Card className="h-full">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Eye className="w-5 h-5" />
                        Report Preview
                      </CardTitle>
                      {results && (
                        <CardDescription>
                          {results.total_rows} rows from {dataSources[config.data_source]?.name || config.data_source}
                        </CardDescription>
                      )}
                    </div>
                    {editingTemplateId && (
                      <Badge variant="outline">
                        Editing: {config.name}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {!results ? (
                    <div className="h-[400px] flex flex-col items-center justify-center text-muted-foreground border-2 border-dashed rounded-lg">
                      <Play className="w-12 h-12 mb-4 opacity-50" />
                      <p className="text-lg font-medium">No Preview Available</p>
                      <p className="text-sm">Configure your report and click "Run Report"</p>
                    </div>
                  ) : results.data.length === 0 ? (
                    <div className="h-[400px] flex flex-col items-center justify-center text-muted-foreground border-2 border-dashed rounded-lg">
                      <FileText className="w-12 h-12 mb-4 opacity-50" />
                      <p className="text-lg font-medium">No Data Found</p>
                      <p className="text-sm">Try adjusting your filters</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-auto max-h-[500px]">
                      <Table>
                        <TableHeader className="sticky top-0 bg-background">
                          <TableRow className="bg-muted/50">
                            {config.selected_fields.map(key => {
                              const field = getCurrentFields().find(f => f.key === key);
                              return (
                                <TableHead key={key} className="whitespace-nowrap">
                                  {field?.name || key}
                                </TableHead>
                              );
                            })}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {results.data.map((row, idx) => (
                            <TableRow key={idx} className="hover:bg-muted/30">
                              {config.selected_fields.map(key => (
                                <TableCell key={key} className="whitespace-nowrap">
                                  {formatCellValue(row[key], key)}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle>Saved Report Templates</CardTitle>
              <CardDescription>Load, edit, or delete your saved report configurations</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {Array(3).fill(0).map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No Saved Templates</p>
                  <p className="text-sm">Create and save a report to see it here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {templates.map(template => (
                    <div
                      key={template.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{template.name}</p>
                            {template.is_public && <Badge variant="outline">Public</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {dataSources[template.data_source]?.name || template.data_source} • {template.selected_fields?.length || 0} fields
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleLoadTemplate(template)}>
                          <Edit className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleLoadTemplate(template)}>
                              <Play className="w-4 h-4 mr-2" /> Run Report
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteTemplate(template.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Template Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTemplateId ? 'Update Template' : 'Save Report Template'}</DialogTitle>
            <DialogDescription>
              Save this report configuration for future use
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Report Name</Label>
              <Input
                value={config.name}
                onChange={(e) => setConfig(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter report name"
                data-testid="template-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={config.description}
                onChange={(e) => setConfig(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this report shows"
                rows={3}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={config.is_public}
                onCheckedChange={(checked) => setConfig(prev => ({ ...prev, is_public: checked }))}
              />
              <Label>Make this template public (visible to all users)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate} disabled={saving} data-testid="save-template-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingTemplateId ? 'Update' : 'Save'} Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
